/**
 * Config Proposal Queue Page — DRR-227
 *
 * Tenant admin approval queue for tenant architect config proposals.
 * Displays proposals with simulation results and allows approve/reject actions.
 *
 * FERP Principles:
 * - Backend-driven: All proposal data from GraphQL queries
 * - Scope-gated: Requires proposal:approve scope (tenant admin)
 * - Audit trail: All actions logged via GraphQL mutations
 * - Life-critical: Simulation results must pass before approval
 */

import { useState, useMemo } from 'react';
import { useDisclosure } from '@mantine/hooks';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  Button,
  Alert,
  Table,
  Loader,
  Modal,
  Textarea,
  Tabs,
  ThemeIcon,
  Divider,
  Code,
  SimpleGrid,
  List,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconClock,
  IconFile,
  IconShieldCheck,
  IconShieldX,
  IconPlayerPlay,
  IconEye,
  IconRefresh,
} from '@tabler/icons-react';
import { useQuery, useMutation } from '@apollo/client/react';
import {
  ListArchitectProposalsDocument,
  ReviewArchitectProposalDocument,
  ActivateArchitectProposalDocument,
  ProposalStatus,
  ConfigProposalType,
} from '@/generated/graphql';

// ============================================================================
// Types
// ============================================================================

interface SimulationSummary {
  passed: boolean;
  errorCount: number;
  warningCount: number;
  errors: string[];
  warnings: string[];
  simulatedAt: string;
}

interface ConfigProposal {
  id: string;
  tenantId: string;
  configType: ConfigProposalType;
  configId: string;
  isNew: boolean;
  title: string;
  description: string;
  status: ProposalStatus;
  simulation: SimulationSummary | null;
  rejectionReason: string | null;
  proposedBy: string;
  proposedAt: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  activatedAt: string | null;
  updatedAt: string;
}

// ============================================================================
// Constants
// ============================================================================

const CONFIG_TYPE_COLORS: Record<string, string> = {
  WORKFLOW: 'violet',
  TASK_GROUP: 'teal',
  FORM_SCHEMA_OVERRIDE: 'blue',
  SETTING: 'orange',
  NAVIGATION_OVERRIDE: 'pink',
  TEMPLATE: 'cyan',
};

const CONFIG_TYPE_LABELS: Record<string, string> = {
  WORKFLOW: 'Workflow',
  TASK_GROUP: 'Task Group',
  FORM_SCHEMA_OVERRIDE: 'Form Schema Override',
  SETTING: 'Setting',
  NAVIGATION_OVERRIDE: 'Navigation Override',
  TEMPLATE: 'Template',
};

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'gray',
  SIMULATING: 'blue',
  SIMULATION_FAILED: 'red',
  PENDING_REVIEW: 'yellow',
  APPROVED: 'green',
  REJECTED: 'red',
  CANCELLED: 'gray',
  ACTIVATED: 'green',
};

// ============================================================================
// Proposal Detail Modal
// ============================================================================

function ProposalDetailModal({
  proposal,
  opened,
  onClose,
  onApprove,
  onReject,
  onActivate,
  loading,
}: {
  proposal: ConfigProposal | null;
  opened: boolean;
  onClose: () => void;
  onApprove: (id: string) => void;
  onReject: (id: string, reason: string) => void;
  onActivate: (id: string) => void;
  loading: boolean;
}) {
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  const handleClose = () => {
    setRejectionReason('');
    setShowRejectForm(false);
    onClose();
  };

  const handleReject = () => {
    if (proposal && rejectionReason.trim()) {
      onReject(proposal.id, rejectionReason.trim());
      handleClose();
    }
  };

  if (!proposal) return null;

  const isPendingReview = proposal.status === ProposalStatus.PendingReview;
  const isApproved = proposal.status === ProposalStatus.Approved;
  const simulationPassed = proposal.simulation?.passed ?? false;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <Text fw={700}>{proposal.title}</Text>
          <Badge variant="light" size="sm" color={CONFIG_TYPE_COLORS[proposal.configType] ?? 'gray'}>
            {CONFIG_TYPE_LABELS[proposal.configType] ?? proposal.configType}
          </Badge>
          <Badge variant="light" size="sm" color={STATUS_COLORS[proposal.status] ?? 'gray'}>
            {proposal.status.replace(/_/g, ' ')}
          </Badge>
        </Group>
      }
      size="lg"
    >
      <Stack gap="md">
        {/* Description */}
        {proposal.description && (
          <Text size="sm" c="dimmed">{proposal.description}</Text>
        )}

        {/* Metadata */}
        <SimpleGrid cols={2} spacing="xs">
          <Group gap="xs">
            <Text size="xs" c="dimmed">Config ID:</Text>
            <Code fz="xs">{proposal.configId}</Code>
          </Group>
          <Group gap="xs">
            <Text size="xs" c="dimmed">Type:</Text>
            <Text size="xs">{proposal.isNew ? 'New Config' : 'Modification'}</Text>
          </Group>
          <Group gap="xs">
            <Text size="xs" c="dimmed">Proposed By:</Text>
            <Text size="xs">{proposal.proposedBy}</Text>
          </Group>
          <Group gap="xs">
            <Text size="xs" c="dimmed">Proposed At:</Text>
            <Text size="xs">{new Date(proposal.proposedAt).toLocaleString()}</Text>
          </Group>
        </SimpleGrid>

        {/* Simulation Results */}
        {proposal.simulation && (
          <>
            <Divider label="Simulation Results" labelPosition="left" />
            <Card withBorder p="sm" bg={simulationPassed ? 'var(--mantine-color-green-light)' : 'var(--mantine-color-red-light)'}>
              <Group gap="xs" mb="xs">
                <ThemeIcon
                  size="sm"
                  variant="light"
                  color={simulationPassed ? 'green' : 'red'}
                >
                  {simulationPassed ? <IconShieldCheck size={14} /> : <IconShieldX size={14} />}
                </ThemeIcon>
                <Text size="sm" fw={600}>
                  {simulationPassed ? 'Simulation Passed' : 'Simulation Failed'}
                </Text>
              </Group>

              <SimpleGrid cols={2} spacing="xs" mb="xs">
                <Text size="xs" c="dimmed">
                  Errors: <Text span fw={600} c="red">{proposal.simulation.errorCount}</Text>
                </Text>
                <Text size="xs" c="dimmed">
                  Warnings: <Text span fw={600} c="yellow">{proposal.simulation.warningCount}</Text>
                </Text>
              </SimpleGrid>

              {proposal.simulation.errors.length > 0 && (
                <List size="xs" icon={<IconX size={10} color="red" />}>
                  {proposal.simulation.errors.map((err, i) => (
                    <List.Item key={i}>{err}</List.Item>
                  ))}
                </List>
              )}

              {proposal.simulation.warnings.length > 0 && (
                <List size="xs" icon={<IconAlertTriangle size={10} color="orange" />}>
                  {proposal.simulation.warnings.map((warn, i) => (
                    <List.Item key={i}>{warn}</List.Item>
                  ))}
                </List>
              )}
            </Card>
          </>
        )}

        {/* Rejection Reason (if rejected) */}
        {proposal.rejectionReason && (
          <>
            <Divider label="Rejection Reason" labelPosition="left" />
            <Alert color="red" icon={<IconX size={16} />}>
              {proposal.rejectionReason}
            </Alert>
          </>
        )}

        {/* Reject Form */}
        {showRejectForm && (
          <>
            <Divider label="Reject Proposal" labelPosition="left" />
            <Textarea
              placeholder="Enter rejection reason (required)..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.currentTarget.value)}
              minRows={3}
            />
            <Group justify="flex-end" gap="xs">
              <Button variant="light" size="sm" onClick={() => setShowRejectForm(false)}>
                Cancel
              </Button>
              <Button
                color="red"
                size="sm"
                onClick={handleReject}
                disabled={!rejectionReason.trim()}
              >
                Confirm Rejection
              </Button>
            </Group>
          </>
        )}

        {/* Actions */}
        {!showRejectForm && (
          <>
            <Divider />
            <Group justify="flex-end" gap="xs">
              {isPendingReview && (
                <>
                  <Button
                    variant="light"
                    color="red"
                    size="sm"
                    leftSection={<IconX size={14} />}
                    onClick={() => setShowRejectForm(true)}
                  >
                    Reject
                  </Button>
                  <Button
                    color="green"
                    size="sm"
                    leftSection={<IconCheck size={14} />}
                    onClick={() => { onApprove(proposal.id); handleClose(); }}
                    loading={loading}
                    disabled={!simulationPassed}
                  >
                    Approve
                  </Button>
                </>
              )}
              {isApproved && (
                <Button
                  color="blue"
                  size="sm"
                  leftSection={<IconPlayerPlay size={14} />}
                  onClick={() => { onActivate(proposal.id); handleClose(); }}
                  loading={loading}
                >
                  Activate
                </Button>
              )}
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}

// ============================================================================
// Stats Cards
// ============================================================================

function ProposalStatsCards({ proposals }: { proposals: ConfigProposal[] }) {
  const stats = useMemo(() => {
    const pending = proposals.filter(p => p.status === ProposalStatus.PendingReview).length;
    const approved = proposals.filter(p => p.status === ProposalStatus.Approved).length;
    const rejected = proposals.filter(p => p.status === ProposalStatus.Rejected).length;
    const activated = proposals.filter(p => p.status === ProposalStatus.Activated).length;
    return { pending, approved, rejected, activated, total: proposals.length };
  }, [proposals]);

  return (
    <SimpleGrid cols={{ base: 2, sm: 5 }} mb="lg">
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="blue"><IconFile size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Total</Text>
        </Group>
        <Text size="lg" fw={700}>{stats.total}</Text>
      </Card>
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="yellow"><IconClock size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Pending</Text>
        </Group>
        <Text size="lg" fw={700}>{stats.pending}</Text>
      </Card>
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="green"><IconCheck size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Approved</Text>
        </Group>
        <Text size="lg" fw={700}>{stats.approved}</Text>
      </Card>
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="red"><IconX size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Rejected</Text>
        </Group>
        <Text size="lg" fw={700}>{stats.rejected}</Text>
      </Card>
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="green"><IconShieldCheck size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Activated</Text>
        </Group>
        <Text size="lg" fw={700}>{stats.activated}</Text>
      </Card>
    </SimpleGrid>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export function ConfigProposalQueuePage() {
  const [selectedProposal, setSelectedProposal] = useState<ConfigProposal | null>(null);
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [activeTab, setActiveTab] = useState<string | null>('pending');

  // Load architect proposals (DRR-227)
  const {
    data,
    loading,
    error,
    refetch,
  } = useQuery(ListArchitectProposalsDocument);

  // Mutations for architect proposals (DRR-227)
  const [reviewProposal, { loading: reviewing }] = useMutation(ReviewArchitectProposalDocument, {
    onCompleted: () => refetch(),
  });

  const [activateProposal, { loading: activating }] = useMutation(ActivateArchitectProposalDocument, {
    onCompleted: () => refetch(),
  });

  const proposals = (data?.architectProposals ?? []) as ConfigProposal[];

  const filteredProposals = useMemo(() => {
    if (activeTab === 'pending') {
      return proposals.filter(p => p.status === ProposalStatus.PendingReview);
    }
    if (activeTab === 'approved') {
      return proposals.filter(p => p.status === ProposalStatus.Approved);
    }
    if (activeTab === 'all') {
      return proposals;
    }
    return proposals;
  }, [proposals, activeTab]);

  const handleApprove = async (proposalId: string) => {
    await reviewProposal({
      variables: {
        input: {
          proposalId,
          approved: true,
        },
      },
    });
  };

  const handleReject = async (proposalId: string, reason: string) => {
    await reviewProposal({
      variables: {
        input: {
          proposalId,
          approved: false,
          reason,
        },
      },
    });
  };

  const handleActivate = async (proposalId: string) => {
    await activateProposal({
      variables: { proposalId },
    });
  };

  const handleOpenProposal = (proposal: ConfigProposal) => {
    setSelectedProposal(proposal);
    openModal();
  };

  const pendingCount = proposals.filter(p => p.status === ProposalStatus.PendingReview).length;
  const approvedCount = proposals.filter(p => p.status === ProposalStatus.Approved).length;

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={2}>Config Proposal Queue</Title>
          <Text size="sm" c="dimmed">
            Review and approve tenant architect config proposals (DRR-227)
          </Text>
        </div>
        <Group gap="xs">
          <Button
            variant="light"
            size="sm"
            leftSection={<IconRefresh size={14} />}
            onClick={() => refetch()}
            loading={loading}
          >
            Refresh
          </Button>
          <Badge variant="light" color="orange" size="lg">Tenant Admin</Badge>
        </Group>
      </Group>

      {error && (
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error" mb="lg">
          {error.message}
        </Alert>
      )}

      {proposals.length > 0 && <ProposalStatsCards proposals={proposals} />}

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List mb="md">
          <Tabs.Tab value="pending" leftSection={<IconClock size={16} />}>
            Pending Review ({pendingCount})
          </Tabs.Tab>
          <Tabs.Tab value="approved" leftSection={<IconCheck size={16} />}>
            Ready to Activate ({approvedCount})
          </Tabs.Tab>
          <Tabs.Tab value="all" leftSection={<IconFile size={16} />}>
            All Proposals
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="pending">
          {loading && (
            <Stack align="center" py="xl">
              <Loader size="lg" />
              <Text c="dimmed">Loading proposals...</Text>
            </Stack>
          )}

          {!loading && filteredProposals.length === 0 && (
            <Alert color="blue" variant="light">
              No proposals pending review.
            </Alert>
          )}

          {!loading && filteredProposals.length > 0 && (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Title</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Simulation</Table.Th>
                  <Table.Th>Proposed By</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredProposals.map((proposal) => (
                  <Table.Tr key={proposal.id}>
                    <Table.Td>
                      <Text size="sm" fw={500}>{proposal.title}</Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>{proposal.description}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" size="sm" color={CONFIG_TYPE_COLORS[proposal.configType] ?? 'gray'}>
                        {CONFIG_TYPE_LABELS[proposal.configType] ?? proposal.configType}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {proposal.simulation ? (
                        <Badge
                          variant="light"
                          size="sm"
                          color={proposal.simulation.passed ? 'green' : 'red'}
                          leftSection={proposal.simulation.passed ? <IconCheck size={10} /> : <IconX size={10} />}
                        >
                          {proposal.simulation.passed ? 'Passed' : 'Failed'}
                        </Badge>
                      ) : (
                        <Badge variant="light" size="sm" color="gray">No Simulation</Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{proposal.proposedBy}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {new Date(proposal.proposedAt).toLocaleDateString()}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Button
                        variant="light"
                        size="xs"
                        leftSection={<IconEye size={12} />}
                        onClick={() => handleOpenProposal(proposal)}
                      >
                        Review
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="approved">
          {!loading && filteredProposals.length === 0 && (
            <Alert color="blue" variant="light">
              No approved proposals waiting to be activated.
            </Alert>
          )}

          {!loading && filteredProposals.length > 0 && (
            <Stack gap="sm">
              {filteredProposals.map((proposal) => (
                <Card key={proposal.id} withBorder p="sm">
                  <Group justify="space-between">
                    <div>
                      <Group gap="xs">
                        <Text fw={500}>{proposal.title}</Text>
                        <Badge variant="light" size="sm" color={CONFIG_TYPE_COLORS[proposal.configType] ?? 'gray'}>
                          {CONFIG_TYPE_LABELS[proposal.configType] ?? proposal.configType}
                        </Badge>
                      </Group>
                      <Text size="xs" c="dimmed">
                        Approved by {proposal.reviewedBy} on {new Date(proposal.reviewedAt!).toLocaleDateString()}
                      </Text>
                    </div>
                    <Button
                      color="blue"
                      size="sm"
                      leftSection={<IconPlayerPlay size={14} />}
                      onClick={() => handleActivate(proposal.id)}
                      loading={activating}
                    >
                      Activate
                    </Button>
                  </Group>
                </Card>
              ))}
            </Stack>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="all">
          {!loading && filteredProposals.length === 0 && (
            <Alert color="blue" variant="light">
              No proposals found.
            </Alert>
          )}

          {!loading && filteredProposals.length > 0 && (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Title</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Proposed By</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredProposals.map((proposal) => (
                  <Table.Tr key={proposal.id}>
                    <Table.Td>
                      <Text size="sm" fw={500}>{proposal.title}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" size="sm" color={CONFIG_TYPE_COLORS[proposal.configType] ?? 'gray'}>
                        {CONFIG_TYPE_LABELS[proposal.configType] ?? proposal.configType}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" size="sm" color={STATUS_COLORS[proposal.status] ?? 'gray'}>
                        {proposal.status.replace(/_/g, ' ')}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{proposal.proposedBy}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {new Date(proposal.proposedAt).toLocaleDateString()}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Button
                        variant="subtle"
                        size="xs"
                        leftSection={<IconEye size={12} />}
                        onClick={() => handleOpenProposal(proposal)}
                      >
                        View
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Tabs.Panel>
      </Tabs>

      {/* Proposal Detail Modal */}
      <ProposalDetailModal
        proposal={selectedProposal}
        opened={modalOpened}
        onClose={closeModal}
        onApprove={handleApprove}
        onReject={handleReject}
        onActivate={handleActivate}
        loading={reviewing || activating}
      />
    </Container>
  );
}
